import { useState } from "react";
import axios from "axios";
import { Send, Check, AlertCircle, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", phone: "", subject: "", message: "", website: "" };

export default function ContactForm() {
    const { t, lang } = useLang();
    const f = t.contact.form;
    const [data, setData] = useState(initial);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | success | error

    const onChange = (e) => {
        const { name, value } = e.target;
        setData((d) => ({ ...d, [name]: value }));
        if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!data.name.trim()) e.name = f.required;
        if (!data.email.trim()) e.email = f.required;
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = f.invalidEmail;
        if (!data.message.trim()) e.message = f.required;
        return e;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        setStatus("sending");
        try {
            await axios.post(`${API}/contact`, { ...data, locale: lang });
            setStatus("success");
            setData(initial);
        } catch (err) {
            console.error(err);
            setStatus("error");
        }
    };

    const Field = ({ name, type = "text", required = false, textarea = false }) => (
        <label className="block">
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-steel-600">
                {f[name]} {required && <span className="text-signal">*</span>}
            </span>
            {textarea ? (
                <textarea
                    name={name}
                    value={data[name]}
                    onChange={onChange}
                    rows={5}
                    data-testid={`contact-form-${name}`}
                    className="mt-2 block w-full bg-white border border-steel-200 focus:border-navy focus:outline-none px-4 py-3 text-navy text-sm leading-relaxed transition-colors"
                />
            ) : (
                <input
                    type={type}
                    name={name}
                    value={data[name]}
                    onChange={onChange}
                    data-testid={`contact-form-${name}`}
                    className="mt-2 block w-full bg-white border border-steel-200 focus:border-navy focus:outline-none px-4 py-3 text-navy text-sm transition-colors"
                />
            )}
            {errors[name] && (
                <span data-testid={`contact-form-error-${name}`} className="block mt-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-signal">
                    {errors[name]}
                </span>
            )}
        </label>
    );

    return (
        <form
            onSubmit={submit}
            data-testid="contact-form"
            noValidate
            className="bg-white border border-steel-100 p-6 lg:p-8 space-y-5"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field name="name" required />
                <Field name="email" type="email" required />
                <Field name="company" />
                <Field name="phone" type="tel" />
            </div>
            <Field name="subject" />
            <Field name="message" textarea required />

            {/* Honeypot — must remain empty; hidden from real users + screen readers */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>
                    Website (leave empty)
                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={data.website}
                        onChange={onChange}
                        data-testid="contact-form-honeypot"
                    />
                </label>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <div className="min-h-[1.25rem]">
                    {status === "success" && (
                        <p data-testid="contact-form-success" className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-wider text-emerald-700">
                            <Check className="h-4 w-4" /> {f.success}
                        </p>
                    )}
                    {status === "error" && (
                        <p data-testid="contact-form-error" className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-wider text-signal">
                            <AlertCircle className="h-4 w-4" /> {f.error}
                        </p>
                    )}
                </div>
                <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-form-submit"
                    className="inline-flex items-center gap-3 bg-navy text-white px-7 py-4 font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors disabled:opacity-60"
                >
                    {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {status === "sending" ? f.sending : f.submit}
                </button>
            </div>
        </form>
    );
}
